import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InnerBlocks, RichText, MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { Button, ToggleControl, TextControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import './editor.scss';

registerBlockType('puroclean/card-content', {
    title: __('Card Content', 'puroclean'),
    parent: ['puroclean/cards-group'],
    category: 'puroclean',
    supports: { 
        reusable: false,
        html: false
    },
    attributes: {
        title: { 
            type: 'string', 
            default: 'Card Title' 
        },
        icon: { 
            type: 'string', 
            default: '' 
        },
        iconId: { 
            type: 'number', 
            default: 0 
        },
        showButton: { 
            type: 'boolean', 
            default: false 
        },
        buttonText: { 
            type: 'string', 
            default: '' 
        },
        buttonUrl: { 
            type: 'string', 
            default: '' 
        },
        buttonAlt: { 
            type: 'string', 
            default: '' 
        },
    },
    edit({ attributes, setAttributes }) {
        const { title, icon, iconId, showButton, buttonText, buttonUrl, buttonAlt } = attributes;
        const blockProps = useBlockProps({
            className: 'bg-secondary-900 text-white p-6 rounded-lg relative w-full'
        });

        return (
            <div {...blockProps}>
                {/* Icon Upload Section */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-white">{__('Icon', 'puroclean')}</label>
                    <MediaUploadCheck>
                        <MediaUpload
                            onSelect={(media) => {
                                setAttributes({ 
                                    iconId: media.id,
                                    icon: media.url
                                });
                            }}
                            allowedTypes={['image']}
                            value={iconId}
                            render={({ open }) => (
                                <div className="relative inline-block">
                                    <div 
                                        className="w-16 h-16 rounded p-2 flex items-center justify-center cursor-pointer border-2 border-dashed border-gray-400 hover:border-gray-300 transition-colors"
                                        onClick={open}
                                        title={icon ? __('Click to change icon', 'puroclean') : __('Click to upload icon', 'puroclean')}
                                    >
                                        {icon ? (
                                            <img 
                                                src={icon} 
                                                alt="Card icon" 
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-gray-400 text-2xl">📁</span>
                                        )}
                                    </div>
                                    
                                    {icon && (
                                        <button 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setAttributes({ icon: '', iconId: 0 });
                                            }}
                                            className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs z-10 hover:bg-red-600"
                                            title="Remove icon"
                                        >
                                            ×
                                        </button>
                                    )}
                                    
                                    <div className="text-xs text-gray-400 mt-1 text-center">
                                        {icon ? __('Click to change', 'puroclean') : __('Click to upload', 'puroclean')}
                                    </div>
                                </div>
                            )}
                        />
                    </MediaUploadCheck>
                </div>

                {/* Title Section */}
                <div className="mb-4">
                    <RichText
                        tagName="h3"
                        className="text-xl font-bold text-white"
                        placeholder={__('Card title...', 'puroclean')}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                </div>

                {/* Content Section - Independent Paragraph Blocks */}
                <div className="mb-6">
                    <div className="flex flex-col rounded min-h-[100px]">
                        <InnerBlocks
                            className="!flex !flex-col gap-1"   /* or gap-2, space-y-2 … */
                            allowedBlocks={['core/paragraph']}
                            template={[
                                ['core/paragraph', { placeholder: 'Add your content here...' }]
                            ]}
                            templateLock={false}
                            renderAppender={InnerBlocks.ButtonBlockAppender}
                            orientation="vertical"
                        />
                    </div>
                </div>

                {/* Button Section */}
                <div className="border-t border-gray-600 pt-4">
                    <ToggleControl
                        label={__('Add Button', 'puroclean')}
                        checked={showButton}
                        onChange={(value) => setAttributes({ showButton: value })}
                    />

                    {showButton && (
                        <div className="space-y-3 mt-4 p-3 bg-gray-700 rounded">
                            <TextControl
                                label={__('Button Text', 'puroclean')}
                                value={buttonText}
                                onChange={(value) => setAttributes({ buttonText: value })}
                            />
                            <TextControl
                                label={__('Button URL', 'puroclean')}
                                value={buttonUrl}
                                onChange={(value) => setAttributes({ buttonUrl: value })}
                            />
                            <TextControl
                                label={__('Button Alt Text', 'puroclean')}
                                value={buttonAlt}
                                onChange={(value) => setAttributes({ buttonAlt: value })}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    },
    save() {
        // Save InnerBlocks content for render.php to use
        return <InnerBlocks.Content />;
    },
}); 